import { CalendarPlus, CircleDollarSign, Undo2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { formatDate, formatMoney } from '@/utils/format'
import { usePayFine, useRenewLoan, useReturnLoan } from '../api'
import { isPositiveAmount } from '../status'
import type { Loan } from '../types'

interface LoanActionsProps {
  loan: Loan
  currency: string
  size?: 'sm' | 'md'
  /** Called after a successful action (e.g. to move focus back to a search box). */
  onDone?: (loan: Loan) => void
}

/** Return / renew / mark-fine-paid buttons for one loan (caller gates on library.manage). */
export function LoanActions({ loan, currency, size = 'sm', onDone }: LoanActionsProps) {
  const { t } = useTranslation('library')
  const returnLoan = useReturnLoan()
  const renew = useRenewLoan()
  const payFine = usePayFine()
  const busy = returnLoan.isPending || renew.isPending || payFine.isPending

  const run = async (action: 'return' | 'renew' | 'pay') => {
    try {
      if (action === 'return') {
        const r = await returnLoan.mutateAsync(loan.id)
        if (isPositiveAmount(r.fineAmount))
          toast.warning(
            t('Returned late'),
            t('{{title}} · fine {{amount}} ({{count}} days)', {
              count: r.daysOverdue,
              title: r.book.title,
              amount: formatMoney(r.fineAmount, currency),
            }),
          )
        else toast.success(t('Book returned'), r.book.title)
        onDone?.(r)
      } else if (action === 'renew') {
        const r = await renew.mutateAsync(loan.id)
        toast.success(
          t('Loan renewed'),
          t('{{title}} · now due {{date}}', { title: r.book.title, date: formatDate(r.dueOn) }),
        )
        onDone?.(r)
      } else {
        const r = await payFine.mutateAsync(loan.id)
        toast.success(t('Fine paid'), `${formatMoney(r.fineAmount, currency)} · ${r.borrower.name}`)
        onDone?.(r)
      }
    } catch (e) {
      toast.error(t('Could not complete the action'), errorMessage(e))
    }
  }

  if (loan.state === 'returned') {
    return loan.fineOutstanding ? (
      <Button
        size={size}
        variant="soft"
        leftIcon={<CircleDollarSign />}
        loading={payFine.isPending}
        disabled={busy}
        onClick={() => void run('pay')}
      >
        {t('Mark paid')}
      </Button>
    ) : null
  }
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {loan.canRenew ? (
        <Button
          size={size}
          variant="secondary"
          leftIcon={<CalendarPlus />}
          loading={renew.isPending}
          disabled={busy}
          onClick={() => void run('renew')}
          aria-label={t('Renew {{title}}', { title: loan.book.title })}
        >
          {t('Renew')}
        </Button>
      ) : null}
      <Button
        size={size}
        variant={loan.state === 'overdue' ? 'danger-soft' : 'soft'}
        leftIcon={<Undo2 />}
        loading={returnLoan.isPending}
        disabled={busy}
        onClick={() => void run('return')}
        aria-label={t('Return {{title}}', { title: loan.book.title })}
      >
        {t('Return')}
      </Button>
    </div>
  )
}
